import { useCallback, useRef, useState } from 'react'
import { pricing } from '@fjalim/shared'
import { transcriptions, uploadFile, type Transcription, type TranscriptionOptions } from '../api'
import { useI18n } from '../i18n'
import { durationBucket, mediaKind, probeDuration, track } from '../lib/analytics'

export type UploadPhase = 'queued' | 'uploading' | 'starting' | 'done' | 'failed'

export interface UploadItem {
  key: string
  name: string
  size: number
  phase: UploadPhase
  /** 0–1 */
  progress: number
  error: string | null
}

const MAX_PARALLEL = 3

/**
 * Upload queue: presigned PUT with progress → complete → create the transcription.
 * `options` are the speaker-label options to create every file with (spec §12); they are read when a job
 * is created rather than when its file was dropped, so a checkbox toggled during a slow upload still
 * counts. `undefined` means nothing was asked for.
 */
export function useUploads(onCreated: (t: Transcription) => void, options?: TranscriptionOptions) {
  const { t, errorText } = useI18n()
  const [items, setItems] = useState<UploadItem[]>([])
  const running = useRef(0)
  const pending = useRef<Array<{ key: string; file: File }>>([])
  const counter = useRef(0)
  // Length of each queued file, read in the browser for the usage counter; only a bucket is ever reported.
  const probes = useRef(new Map<File, Promise<number | undefined>>())
  const onCreatedRef = useRef(onCreated)
  onCreatedRef.current = onCreated
  const optionsRef = useRef(options)
  optionsRef.current = options

  const patch = useCallback((key: string, change: Partial<UploadItem>) => {
    setItems((all) => all.map((it) => (it.key === key ? { ...it, ...change } : it)))
  }, [])

  const pump = useCallback(() => {
    while (running.current < MAX_PARALLEL && pending.current.length > 0) {
      const job = pending.current.shift()!
      running.current++
      patch(job.key, { phase: 'uploading' })
      void (async () => {
        try {
          const { uploadId } = await uploadFile(job.file, (p) =>
            patch(job.key, { progress: p.total > 0 ? p.loaded / p.total : 0 }),
          )
          patch(job.key, { phase: 'starting', progress: 1 })
          const created = await transcriptions.create(uploadId, optionsRef.current)
          onCreatedRef.current(created)
          const probe = probes.current.get(job.file)
          probes.current.delete(job.file)
          const speakers = optionsRef.current?.speakers === true
          void (probe ?? Promise.resolve(undefined)).then((seconds) =>
            track('upload', { kind: mediaKind(job.file), duration: durationBucket(seconds), speakers }),
          )
          patch(job.key, { phase: 'done' })
        } catch (error) {
          const message =
            error instanceof Error && error.message.startsWith('upload_')
              ? t('list.upload.failed')
              : errorText(error)
          patch(job.key, { phase: 'failed', error: message })
        } finally {
          probes.current.delete(job.file)
          running.current--
          pump()
        }
      })()
    }
  }, [patch, t, errorText])

  const add = useCallback(
    (files: File[]) => {
      const fresh: UploadItem[] = []
      for (const file of files) {
        if (file.size <= pricing.maxUploadBytes) probes.current.set(file, probeDuration(file))
        const key = `u${++counter.current}`
        const tooBig = file.size > pricing.maxUploadBytes
        fresh.push({
          key,
          name: file.name,
          size: file.size,
          phase: tooBig ? 'failed' : 'queued',
          progress: 0,
          error: tooBig ? t('list.upload.tooBig') : null,
        })
        if (!tooBig) pending.current.push({ key, file })
      }
      setItems((all) => [...fresh, ...all])
      pump()
    },
    [pump, t],
  )

  const dismiss = useCallback((key: string) => setItems((all) => all.filter((it) => it.key !== key)), [])

  return { items, add, dismiss }
}
